"use client"

import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import React, { useRef, useLayoutEffect } from "react"

import PageGrid from "../../PageGrid"

gsap.registerPlugin(ScrollTrigger)

type AnimatedBullseyeProps = {
  heading: string
  subheading: string
  pillarOne: string
  pillarTwo: string
  pillarThree: string
}

const AnimatedBullseyeSection: React.FC<AnimatedBullseyeProps> = (
  props: AnimatedBullseyeProps,
) => {
  const { heading, subheading, pillarOne, pillarTwo, pillarThree } = props
  const animationRootScope = useRef(null)
  const tl = useRef(gsap.timeline())

  useLayoutEffect(() => {
    // -- DOM elements ready for animation --
    const ctx = gsap.context(() => {
      tl.current = gsap.timeline({
        scrollTrigger: {
          trigger: "#circle3",
          start: "top bottom",
          scrub: 0.5,
          invalidateOnRefresh: true,
        },
      })
      // first setup the pre-animation states
      gsap.set(["#circle3, #circle2, #circle1"], { borderColor: "transparent" })
      gsap.set(["#pillar1,#pillar2,#pillar3"], { autoAlpha: 0 })

      tl.current.to("#circle3", { backgroundColor: "#F4FFE6" })
      tl.current.to(
        ["#circle3, #circle2, #circle1"],
        {
          borderColor: "#F4FFE6",
        },
        "<",
      )
      tl.current.to("#pillar1", { autoAlpha: 1, color: "#33A3FF" }, "<")
      tl.current.to("#circle2", { backgroundColor: "#99D8FF" })
      tl.current.to("#pillar2", { autoAlpha: 1, color: "#33A3FF" }, "<")
      tl.current.to("#pillar1", { color: "#E5F8FF" }, "<")
      tl.current.to("#circle1", { backgroundColor: "#008cff" })
      tl.current.to("#pillar3", { autoAlpha: 1, color: "#33A3FF" }, "<")
      tl.current.to("#pillar2", { color: "#E5F8FF" }, "<")

      ScrollTrigger.create({
        trigger: animationRootScope.current,
        pin: true,
        pinSpacing: true, // Ensure spacing is maintained
        start: "top top", // Adjust start point
        end: "bottom top+=100", // Adjust end point to fit your needs
        onEnter: () => {
          animationRootScope.current.style.backgroundColor = "#gray-90"
        },
        onLeave: () => {
          animationRootScope.current.style.backgroundColor = "#gray-90"
        },
      })
    }, animationRootScope)

    return () => {
      // -- cleanup code --
      ctx.revert()
      tl.current.kill()
    }
  }, [])

  return (
    <section
      ref={animationRootScope}
      className="pt-10 pb-[30px] md:py-20 xl:py-28 bg-gray-90"
    >
      <PageGrid id="pinnable">
        <div className="p-4 col-span-full xl:mb-[55px] md:mb-12 mb-10">
          <h2 className="text-gray-2.5 heading1 md:headline5 xl:headline4 mb-4">
            {heading}
          </h2>
          <p className="text-gray-2.5 body3 xl:body1">{subheading}</p>
        </div>
        {/* this is where our animated circle with pillar text will go */}
        <div className="flex flex-col justify-center p-4 lg:col-start-2 lg:col-span-5 md:col-span-2 col-span-full">
          <div
            id="pillar1"
            className="heading3 md:heading1 xl:headline5 mb-3 md:mb-6 text-gray-2.5"
          >
            {pillarOne}
          </div>
          <div
            id="pillar2"
            className="heading3 md:heading1 xl:headline5 mb-3 md:mb-6 text-gray-2.5"
          >
            {pillarTwo}
          </div>
          <div
            id="pillar3"
            className="heading3 md:heading1 xl:headline5 text-gray-2.5"
          >
            {pillarThree}
          </div>
        </div>
        <div className="lg:col-start-7 lg:col-span-4 md:col-span-2 col-span-full">
          {/* small number divided by large number to percentage for inner circles */}
          <div
            id="circle1"
            className="md:w-[338px] md:h-[338px] w-[310px] h-[310px] rounded-full border border-bg-blue-5 flex items-center justify-center"
          >
            <div
              id="circle2"
              className="rounded-full border border-bg-blue-5 w-[72%] h-[72%] flex items-center justify-center"
            >
              <div
                id="circle3"
                className="rounded-full border border-bg-blue-5 w-[55%] h-[55%]"
              />
            </div>
          </div>
        </div>
      </PageGrid>
    </section>
  )
}

export default AnimatedBullseyeSection
